// inv/src/pages/payment/PaymentProcess.jsx
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { useMembershipAccess } from '../../hooks/membership';
import { useAuth } from '../../components/authentication/AuthContext';
import { clearPaddleEventCallback, initializeOrUpdatePaddle } from '../../lib/paddleClient';

export default function PaymentProcess({
  priceId,
  userId,
  paddleSubscriptionId,
  interval,
  onClose,
  onSuccess,
  userStatus,
  pendingMembershipType,
  planName,
}) {
  const [paddleReady, setPaddleReady] = useState(false);
  const openedRef = useRef(false);
  const checkoutCompletedRef = useRef(false);
  const completedTimeRef = useRef(null);
  const { user } = useAuth();
  const membership = useMembershipAccess({
    user: {
      status: userStatus,
      pendingMembershipType,
      paddleSubscriptionId,
    },
    loading: false,
  });

  useEffect(() => {
    const oOpen = XMLHttpRequest.prototype.open;
    const oSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (m, url, ...rest) {
      this.__logUrl = url;
      return oOpen.call(this, m, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function (body) {
      console.groupCollapsed(`📡 XHR → ${this.__logUrl}`);
      console.log('Body:', body);
      this.addEventListener('load', () => {
        console.log('Status:', this.status);
        console.log('Response:', this.responseText);
        console.groupEnd();
      });
      return oSend.call(this, body);
    };

    const oFetch = window.fetch;
    window.fetch = (input, init) => {
      const url = typeof input === 'string' ? input : input.url;
      console.groupCollapsed(`🌐 fetch → ${url}`);
      console.log('Init:', init);
      return oFetch(input, init).then((res) => {
        res
          .clone()
          .text()
          .then((txt) => {
            console.log('Status:', res.status);
            console.log('Response:', txt);
            console.groupEnd();
          })
          .catch(() => console.groupEnd());
        return res;
      });
    };

    return () => {
      XMLHttpRequest.prototype.open = oOpen;
      XMLHttpRequest.prototype.send = oSend;
      window.fetch = oFetch;
    };
  }, []);

  useEffect(() => {
    checkoutCompletedRef.current = false;
    completedTimeRef.current = null;

    const handlePaddleEvents = async (ev) => {
      switch (ev?.name) {
        case 'checkout.completed':
          console.log('✅ checkout.completed', { priceId, interval, eventId: ev.id });
          checkoutCompletedRef.current = true;
          completedTimeRef.current = Date.now();
          try {
            const payload = {
              planName: planName || 'your plan',
              immediate: null,
              recurring: null,
              credit: 0,
              currency: 'USD',
              nextBilledAt: null,
              downgrade: false,
            };
            sessionStorage.setItem('ty-payload', JSON.stringify(payload));
          } catch { }

          axios.post(`${API_URL}/user/clear-incomplete-checkout`, {}, { withCredentials: true })
            .catch(e => console.error('Failed to clear incompleteCheckout:', e));

          window.Paddle.Checkout.close(ev?.id);
          window.location.assign('/thank-you');
          break;

        case 'checkout.closed':
          const timeSinceCompleted = completedTimeRef.current ? Date.now() - completedTimeRef.current : Infinity;
          const shouldMarkIncomplete = !checkoutCompletedRef.current && timeSinceCompleted > 5000;

          console.log('🔄 checkout.closed event:', {
            checkoutCompleted: checkoutCompletedRef.current,
            timeSinceCompleted,
            shouldMarkIncomplete,
            priceId,
            interval
          });

          if (shouldMarkIncomplete) {
            try {
              await axios.post(`${API_URL}/user/set-incomplete-checkout`, {
                planId: priceId,
                interval,
              }, {
                withCredentials: true,
              });
            } catch (err) {
              console.error('❌ Failed to store incomplete checkout:', err);
            }
          } else {
            console.log('🚫 Not marking as incomplete checkout - payment was completed or too soon after completion');
          }
          onClose();
          break;

        case 'checkout.error':
          console.error('❌ Paddle error', ev);
          onClose();
          break;

        default:
          break;
      }
    };

    let cancelled = false;

    initializeOrUpdatePaddle({ user })
      .then((Paddle) => {
        if (!cancelled && Paddle) {
          Paddle.Update({ eventCallback: handlePaddleEvents });
          setPaddleReady(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('❌ Paddle setup failed', error);
          onClose();
        }
      });

    return () => {
      cancelled = true;
      clearPaddleEventCallback();
    };
  }, [onClose, priceId, interval, planName, user?.paddleUserId, user?.paddleCustomerId]);

  useEffect(() => {
    if (!paddleReady) return;
    if (openedRef.current) return;
    openedRef.current = true;

    const shouldCreateNewSubscription = membership.shouldCreateCheckoutSubscription;

    const opts = shouldCreateNewSubscription
      ? { items: [{ priceId, quantity: 1 }] }
      : { subscription: paddleSubscriptionId, items: [{ priceId, quantity: 1 }] };

    opts.customData = { user_id: userId };

    if (!window.Paddle?.Checkout) return;

    console.log('➡️ Paddle.Checkout.open()', opts);
    window.Paddle.Checkout.open(opts);
  }, [membership.shouldCreateCheckoutSubscription, paddleReady, priceId, paddleSubscriptionId, userId]);

  return null;
}
